import i18next from 'i18next';
import { EContentProductDetailPosition, ELanguageCode } from '__generated__/graphql';
import type { ProductDetailsSelectorInput } from 'semshared/pricelist/producttextselector';
import {
  getSeminarRoomDisplayHeader,
  isSeminarRoomFacility,
  type SeminarRoomFacilityLike,
} from 'semshared/pricelist/quickprice_lister';

type ProductTextGetter = {
  getProductText: (input: ProductDetailsSelectorInput) => string | null;
};

function cmsHeaderEn(selector: ProductTextGetter, sku: string, hotelId: number | null): string | null {
  const short = selector.getProductText({
    sku,
    hotelId,
    position: EContentProductDetailPosition.HEADER_SHORT,
    language: ELanguageCode.en,
  });
  if (short) return short;
  return selector.getProductText({
    sku,
    hotelId,
    position: EContentProductDetailPosition.HEADER,
    language: ELanguageCode.en,
  });
}

function lookupFromI18n(lookupCode: string | null | undefined, entity: 'product' | 'bundle' | 'facility'): string | null {
  if (!lookupCode) return null;
  if (entity === 'facility') {
    const fk = `pricelist-facility-${lookupCode}`;
    const ft = i18next.t(fk);
    if (ft !== fk) return ft;
  }
  const pk = `pricelist-lookup-${lookupCode}`;
  const pt = i18next.t(pk);
  if (pt !== pk) return pt;
  return null;
}

export function quickPriceSkuDisplayName(
  productTextSelector: ProductTextGetter | null | undefined,
  hotelId: number | null | undefined,
  sku: string,
  fallbackName: string,
  lookupCode: string | null | undefined,
  entity: 'product' | 'bundle' | 'facility' = 'product',
  facilityRef?: SeminarRoomFacilityLike | null,
): string {
  if (entity === 'facility' && isSeminarRoomFacility(facilityRef ?? { lookupCode })) {
    return getSeminarRoomDisplayHeader(i18next.language);
  }

  if (!(i18next.language || 'de').startsWith('en')) {
    return fallbackName;
  }
  if (productTextSelector) {
    const fromCms = cmsHeaderEn(productTextSelector, sku, hotelId ?? null);
    if (fromCms) return fromCms;
  }
  const fromLookup = lookupFromI18n(lookupCode, entity);
  if (fromLookup) return fromLookup;
  return fallbackName;
}

type OfferHistoryServiceDetailLike = {
  sku: string;
  serviceName: string;
  displayName?: string | null;
  lookupCode?: string | null;
  facilityId?: number | null;
  productId?: number | null;
  bundleId?: number | null;
};

/** Display name for offer history service rows (masks seminar room facility names like the editor). */
export function offerHistoryServiceDisplayName(
  detail: OfferHistoryServiceDetailLike,
  hotelId?: number | null,
  productTextSelector?: ProductTextGetter | null,
): string {
  const fallback = detail.displayName || detail.serviceName;
  if (detail.sku.trim() === 'MANUAL') {
    return fallback;
  }
  const isFacilityRow = !!detail.facilityId && !detail.productId && !detail.bundleId;

  // Facility history rows omit lookupCode; they are seminar rooms from event room changes.
  if (isFacilityRow || isSeminarRoomFacility({ lookupCode: detail.lookupCode })) {
    return getSeminarRoomDisplayHeader(i18next.language);
  }

  const entity = detail.bundleId ? 'bundle' : 'product';
  return quickPriceSkuDisplayName(
    productTextSelector,
    hotelId ?? undefined,
    detail.sku,
    fallback,
    detail.lookupCode ?? undefined,
    entity,
  );
}
